import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, PaginatedResponse } from '../types';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1/admin',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - error handling
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    
    if (error.response?.data) {
      const errorData = error.response.data as { code?: number; message?: string };
      if (errorData.message) {
        const customError = new Error(errorData.message);
        (customError as { response?: typeof error.response }).response = error.response;
        return Promise.reject(customError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API methods
const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<unknown, ApiResponse<T>>(url, config),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<unknown, ApiResponse<T>>(url, data, config),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<unknown, ApiResponse<T>>(url, data, config),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<unknown, ApiResponse<T>>(url, data, config),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<unknown, ApiResponse<T>>(url, config),
};

// Auth API
export const authApi = {
  login: (username: string, password: string) =>
    api.post<{
      user: {
        id: string;
        username: string;
        email: string;
        role: string;
        permissions: string[];
      };
      token: string;
    }>('/auth/login', { username, password }),

  logout: () => api.post<void>('/auth/logout'),

  getCurrentUser: () =>
    api.get<{
      id: string;
      username: string;
      email: string;
      role: string;
      permissions: string[];
    }>('/auth/me'),

  changePassword: (oldPassword: string, newPassword: string) =>
    api.post<void>('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () =>
    api.get<{
      newUsersToday: number;
      activeUsersToday: number;
      runsToday: number;
      aiCallsToday: number;
      revenueToday: number;
      tokensToday: number;
      aiCostToday: number;
      avgCostPerUser: number;
      totalRunsToday: number;
      successRate: number;
      failureRate: number;
      avgExecutionTime: number;
      userTrend: { date: string; value: number }[];
      runTrend: { date: string; value: number }[];
      aiCostTrend: { date: string; value: number }[];
      revenueTrend: { date: string; value: number }[];
    }>('/dashboard/stats'),

  getTopFailures: () =>
    api.get<{
      workflowFailures: Array<{
        workflowId: string;
        workflowName: string;
        failureCount: number;
        failureRate: number;
      }>;
      aiFailures: Array<{
        errorType: string;
        count: number;
      }>;
      webhookFailures: Array<{
        webhookId: string;
        failureCount: number;
      }>;
    }>('/dashboard/top-failures'),
};

// User Management API
export const userApi = {
  listUsers: (params?: {
    page?: number;
    pageSize?: number;
    keyword?: string;
    status?: string;
    role?: string;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      username: string;
      email: string;
      nickname?: string;
      avatar?: string;
      role: string;
      status: string;
      emailVerified: boolean;
      lastLoginAt?: string;
      createdAt: string;
      teamCount: number;
      workflowCount: number;
      aiUsageCount: number;
    }>>('/users', { params }),

  getUserDetail: (id: string) =>
    api.get<{
      id: string;
      username: string;
      email: string;
      nickname?: string;
      avatar?: string;
      role: string;
      status: string;
      emailVerified: boolean;
      ip?: string;
      country?: string;
      device?: string;
      lastLoginAt?: string;
      createdAt: string;
      loginCount: number;
      teamCount: number;
      workflowCount: number;
      runCount: number;
      aiUsageCount: number;
      tokenUsed: number;
      storageUsed: number;
      recentLogins: Array<{
        id: string;
        time: string;
        ip: string;
        device: string;
      }>;
      recentRuns: Array<{
        id: string;
        workflowId: string;
        workflowName: string;
        status: string;
        startedAt: string;
        duration: number;
      }>;
      recentAICalls: Array<{
        id: string;
        model: string;
        tokens: number;
        cost: number;
        createdAt: string;
      }>;
    }>(`/users/${id}`),

  resetPassword: (id: string, newPassword: string) =>
    api.post<void>(`/users/${id}/reset-password`, { newPassword }),

  freezeUser: (id: string, reason?: string) =>
    api.post<void>(`/users/${id}/freeze`, { reason }),

  unfreezeUser: (id: string) =>
    api.post<void>(`/users/${id}/unfreeze`),

  deleteUser: (id: string) =>
    api.delete<void>(`/users/${id}`),

  grantQuota: (id: string, quota: { type: string; amount: number; expiresAt?: string }) =>
    api.post<void>(`/users/${id}/grant-quota`, quota),

  upgradeSubscription: (id: string, plan: string, durationMonths: number) =>
    api.post<void>(`/users/${id}/upgrade-subscription`, { plan, durationMonths }),
};

// Team Management API
export const teamApi = {
  listTeams: (params?: {
    page?: number;
    pageSize?: number;
    keyword?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      name: string;
      ownerId: string;
      ownerName: string;
      ownerEmail: string;
      memberCount: number;
      workflowCount: number;
      aiUsage: number;
      createdAt: string;
      status: string;
    }>>('/teams', { params }),

  getTeamDetail: (id: string) =>
    api.get<{
      id: string;
      name: string;
      ownerId: string;
      ownerName: string;
      ownerEmail: string;
      memberCount: number;
      workflowCount: number;
      aiUsage: number;
      createdAt: string;
      status: string;
      members: Array<{
        id: string;
        userId: string;
        username: string;
        email: string;
        avatar?: string;
        role: string;
        joinedAt: string;
      }>;
      workflows: Array<{
        id: string;
        name: string;
        nodeCount: number;
        runCount: number;
        createdAt: string;
      }>;
      recentRuns: Array<{
        id: string;
        workflowId: string;
        workflowName: string;
        status: string;
        startedAt: string;
        duration: number;
      }>;
    }>(`/teams/${id}`),

  deleteTeam: (id: string) =>
    api.delete<void>(`/teams/${id}`),

  updateMemberRole: (teamId: string, memberId: string, role: string) =>
    api.put<void>(`/teams/${teamId}/members/${memberId}/role`, { role }),

  removeMember: (teamId: string, memberId: string) =>
    api.delete<void>(`/teams/${teamId}/members/${memberId}`),
};

// Workflow Management API
export const workflowApi = {
  listWorkflows: (params?: {
    page?: number;
    pageSize?: number;
    keyword?: string;
    status?: string;
    userId?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      name: string;
      description?: string;
      userId: string;
      username: string;
      nodeCount: number;
      runCount: number;
      lastRunAt?: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    }>>('/workflows', { params }),

  getWorkflowDetail: (id: string) =>
    api.get<{
      id: string;
      name: string;
      description?: string;
      userId: string;
      username: string;
      nodeCount: number;
      runCount: number;
      lastRunAt?: string;
      status: string;
      createdAt: string;
      updatedAt: string;
      definition: {
        nodes: Array<{
          id: string;
          type: string;
          label: string;
          position: { x: number; y: number };
          config?: Record<string, unknown>;
        }>;
        edges: Array<{
          id: string;
          source: string;
          target: string;
        }>;
      };
      stats: {
        totalRuns: number;
        successRate: number;
        avgExecutionTime: number;
      };
      runs: Array<{
        id: string;
        status: string;
        startedAt: string;
        endedAt?: string;
        duration: number;
      }>;
    }>(`/workflows/${id}`),

  disableWorkflow: (id: string, reason?: string) =>
    api.post<void>(`/workflows/${id}/disable`, { reason }),

  enableWorkflow: (id: string) =>
    api.post<void>(`/workflows/${id}/enable`),

  cloneWorkflow: (id: string) =>
    api.post<{ id: string }>(`/workflows/${id}/clone`),

  debugRun: (id: string, testData?: Record<string, unknown>) =>
    api.post<{
      runId: string;
      status: string;
      result?: Record<string, unknown>;
      error?: string;
    }>(`/workflows/${id}/debug-run`, { testData }),

  exportWorkflow: (id: string, workflowName: string) => {
    return apiClient.get(`/workflows/${id}/export`, {
      responseType: 'blob',
    }).then((response: unknown) => {
      // The response interceptor already unwraps response.data,
      // so `response` here is the raw Blob directly.
      const blob = response instanceof Blob
        ? response
        : new Blob([response as BlobPart], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${workflowName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { code: 0, data: null };
    });
  },
};

// Execution API
export const executionApi = {
  listExecutions: (params?: {
    page?: number;
    pageSize?: number;
    workflowId?: string;
    userId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      workflowId: string;
      workflowName: string;
      userId: string;
      username: string;
      status: string;
      startedAt: string;
      endedAt?: string;
      duration: number;
      errorMessage?: string;
    }>>('/executions', { params }),

  getExecutionDetail: (id: string) =>
    api.get<{
      id: string;
      workflowId: string;
      workflowName: string;
      userId: string;
      username: string;
      status: string;
      startedAt: string;
      endedAt?: string;
      duration: number;
      errorMessage?: string;
      nodes: Array<{
        id: string;
        nodeId: string;
        nodeType: string;
        nodeLabel: string;
        status: string;
        startedAt?: string;
        endedAt?: string;
        duration: number;
        errorMessage?: string;
      }>;
      inputData?: Record<string, unknown>;
      outputData?: Record<string, unknown>;
      errorStack?: string;
    }>(`/executions/${id}`),
};

// AI Usage API
export const aiApi = {
  getUsageStats: (params?: {
    dateFrom?: string;
    dateTo?: string;
    groupBy?: 'user' | 'team' | 'workflow' | 'model';
  }) =>
    api.get<{
      totalCalls: number;
      totalTokens: number;
      totalCost: number;
      items: Array<{
        id: string;
        name: string;
        calls: number;
        tokens: number;
        cost: number;
      }>;
    }>('/ai/usage', { params }),

  getCostByModel: (params?: { dateFrom?: string; dateTo?: string }) =>
    api.get<Array<{
      model: string;
      calls: number;
      tokens: number;
      cost: number;
    }>>('/ai/cost-by-model', { params }),

  getDailyCost: (params?: { dateFrom?: string; dateTo?: string }) =>
    api.get<Array<{
      date: string;
      cost: number;
      tokens: number;
      calls: number;
    }>>('/ai/daily-cost', { params }),

  getAnomalies: () =>
    api.get<Array<{
      id: string;
      type: string;
      userId?: string;
      username?: string;
      description: string;
      detectedAt: string;
      severity: string;
    }>>('/ai/anomalies'),
};

// Subscription API
export const subscriptionApi = {
  listSubscriptions: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    plan?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      userId: string;
      username: string;
      email: string;
      plan: string;
      price: number;
      currency: string;
      startedAt: string;
      renewAt?: string;
      status: string;
      provider: string;
    }>>('/subscriptions', { params }),

  listPayments: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      userId: string;
      username: string;
      amount: number;
      currency: string;
      status: string;
      provider: string;
      providerPaymentId: string;
      createdAt: string;
    }>>('/payments', { params }),

  grantSubscription: (userId: string, data: {
    plan: string;
    durationMonths: number;
    reason?: string;
  }) =>
    api.post<void>(`/subscriptions/grant/${userId}`, data),

  extendSubscription: (subscriptionId: string, durationMonths: number) =>
    api.post<void>(`/subscriptions/${subscriptionId}/extend`, { durationMonths }),

  processRefund: (paymentId: string, reason?: string) =>
    api.post<void>(`/payments/${paymentId}/refund`, { reason }),
};

// Template API
export const templateApi = {
  listTemplates: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    keyword?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      name: string;
      description?: string;
      authorId: string;
      authorName: string;
      downloads: number;
      rating: number;
      status: string;
      createdAt: string;
      updatedAt: string;
    }>>('/templates', { params }),

  getTemplateDetail: (id: string) =>
    api.get<{
      id: string;
      name: string;
      description?: string;
      authorId: string;
      authorName: string;
      downloads: number;
      rating: number;
      status: string;
      createdAt: string;
      updatedAt: string;
      definition: {
        nodes: Array<{
          id: string;
          type: string;
          label: string;
          position: { x: number; y: number };
          config?: Record<string, unknown>;
        }>;
        edges: Array<{
          id: string;
          source: string;
          target: string;
        }>;
      };
      nodeCount: number;
      usageStats: {
        totalDownloads: number;
        totalUses: number;
        avgRating: number;
      };
    }>(`/templates/${id}`),

  createTemplate: (data: {
    name: string | { zh: string; en: string; ja?: string };
    description?: string | { zh: string; en: string; ja?: string };
    category?: string;
    industry?: string;
    tags?: string[] | { zh: string[]; en: string[]; ja?: string[] };
    file?: File;
  }) => {
    const formData = new FormData();
    formData.append('name', JSON.stringify(data.name));
    if (data.description) formData.append('description', JSON.stringify(data.description));
    if (data.category) formData.append('category', data.category);
    if (data.industry) formData.append('industry', data.industry);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));
    if (data.file) formData.append('file', data.file);
    
    return api.post<{
      id: string;
      name: string | { zh: string; en: string; ja?: string };
      description?: string | { zh: string; en: string; ja?: string };
      category: string;
      industry?: string;
      tags?: string[] | { zh: string[]; en: string[]; ja?: string[] };
      status: string;
      nodeCount: number;
      createdAt: string;
    }>('/templates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  approveTemplate: (id: string) =>
    api.post<void>(`/templates/${id}/approve`),

  rejectTemplate: (id: string, reason: string) =>
    api.post<void>(`/templates/${id}/reject`, { reason }),

  deleteTemplate: (id: string) =>
    api.delete<void>(`/templates/${id}`),

  updateTemplateStatus: (id: string, status: 'active' | 'inactive' | 'draft') =>
    api.patch<void>(`/templates/${id}/status`, { status }),

  exportTemplate: (id: string, templateName: string) => {
    return apiClient.get(`/templates/${id}/export`, {
      responseType: 'blob',
    }).then((response: unknown) => {
      const blob = response instanceof Blob
        ? response
        : new Blob([response as BlobPart], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { code: 0, data: null };
    });
  },

  importTemplate: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post<{
      id: string;
      name: string;
      description?: string;
      category: string;
      industry?: string;
      tags?: string[];
      status: string;
      nodeCount: number;
      createdAt: string;
    }>('/templates/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  updateTemplate: (id: string, data: {
    name?: string | { zh: string; en: string; ja?: string };
    description?: string | { zh: string; en: string; ja?: string };
    category?: string;
    industry?: string;
    tags?: string[] | { zh: string[]; en: string[]; ja?: string[] };
    file?: File;
  }) => {
    const formData = new FormData();
    if (data.name) formData.append('name', JSON.stringify(data.name));
    if (data.description) formData.append('description', JSON.stringify(data.description));
    if (data.category) formData.append('category', data.category);
    if (data.industry) formData.append('industry', data.industry);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));
    if (data.file) formData.append('file', data.file);
    
    return api.put<{
      id: string;
      name: string;
      description?: string;
      category: string;
      industry?: string;
      tags?: string[];
      status: string;
      updatedAt: string;
    }>(`/templates/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  translateTemplateFields: (text: string, targetLang: string[], sourceLang: string = 'zh') => {
    return api.post<{
      translations: Record<string, string>;
    }>('/templates/translate', {
      text,
      targetLang,
      sourceLang,
    });
  },
};

// File Storage API
export const fileApi = {
  listFiles: (params?: {
    page?: number;
    pageSize?: number;
    userId?: string;
    type?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      userId: string;
      username: string;
      name: string;
      size: number;
      type: string;
      mimeType: string;
      uploadedAt: string;
    }>>('/files', { params }),

  getStorageStats: () =>
    api.get<{
      totalSize: number;
      totalFiles: number;
      userRanking: Array<{
        userId: string;
        username: string;
        totalSize: number;
        fileCount: number;
      }>;
    }>('/files/stats'),

  deleteFile: (id: string) =>
    api.delete<void>(`/files/${id}`),
};

// API Key Management API
export const apiKeyApi = {
  listAPIKeys: (params?: {
    page?: number;
    pageSize?: number;
    userId?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      userId: string;
      username: string;
      name: string;
      keyPreview: string;
      createdAt: string;
      lastUsedAt?: string;
      status: string;
    }>>('/api-keys', { params }),
};

// Webhook API
export const webhookApi = {
  listWebhookLogs: (params?: {
    page?: number;
    pageSize?: number;
    webhookId?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      webhookId: string;
      url: string;
      triggeredAt: string;
      responseTime: number;
      httpStatus: number;
      success: boolean;
    }>>('/webhook-logs', { params }),
};

// Risk Control API
export const riskApi = {
  listAlerts: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    severity?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      type: string;
      severity: string;
      userId?: string;
      username?: string;
      description: string;
      detectedAt: string;
      status: string;
    }>>('/risk/alerts', { params }),

  updateAlertStatus: (id: string, status: string) =>
    api.put<void>(`/risk/alerts/${id}/status`, { status }),

  getRiskSettings: () =>
    api.get<{
      maxAILCallsPerHour: number;
      maxWorkflowRunsPerHour: number;
      maxRegistrationsPerIP: number;
      blockedIPs: string[];
    }>('/risk/settings'),

  updateRiskSettings: (settings: {
    maxAILCallsPerHour?: number;
    maxWorkflowRunsPerHour?: number;
    maxRegistrationsPerIP?: number;
    blockedIPs?: string[];
  }) =>
    api.put<void>('/risk/settings', settings),
};

// System Log API
export const logApi = {
  listLogs: (params?: {
    page?: number;
    pageSize?: number;
    level?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      level: string;
      category: string;
      message: string;
      details?: Record<string, unknown>;
      createdAt: string;
    }>>('/logs', { params }),
};

// Activation Code API
export const activationCodeApi = {
  listCodes: (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    plan?: string;
    keyword?: string;
  }) =>
    api.get<PaginatedResponse<{
      id: string;
      code: string;
      plan: string;
      durationDays: number;
      status: string;
      createdBy: string;
      createdAt: string;
      expiresAt?: string;
      usedBy?: string;
      usedAt?: string;
      maxUses?: number;
      useCount: number;
      description?: string;
    }>>('/activation-codes', { params }),

  createCodes: (data: {
    plan: string;
    durationDays: number;
    quantity: number;
    expiresAt?: string;
    maxUses?: number;
    description?: string;
    prefix?: string;
  }) =>
    api.post<{
      codes: string[];
    }>('/activation-codes', data),

  disableCode: (id: string) =>
    api.post<void>(`/activation-codes/${id}/disable`),

  enableCode: (id: string) =>
    api.post<void>(`/activation-codes/${id}/enable`),

  deleteCode: (id: string) =>
    api.delete<void>(`/activation-codes/${id}`),

  getCodeDetail: (id: string) =>
    api.get<{
      id: string;
      code: string;
      plan: string;
      durationDays: number;
      status: string;
      createdBy: string;
      createdAt: string;
      expiresAt?: string;
      usedBy?: string;
      usedAt?: string;
      maxUses?: number;
      useCount: number;
      description?: string;
      useHistory: Array<{
        userId: string;
        username: string;
        usedAt: string;
      }>;
    }>(`/activation-codes/${id}`),
};

// System Config API
export const configApi = {
  getConfig: () =>
    api.get<{
      ai: {
        defaultModel: string;
        maxTokensPerCall: number;
        maxTokensPerDay: number;
        enabledModels: string[];
      };
      limits: {
        maxWorkflowsPerUser: number;
        maxFileSize: number;
        maxExecutionTime: number;
        maxNodesPerWorkflow: number;
      };
      features: {
        enableAI: boolean;
        enableTeams: boolean;
        enableTemplates: boolean;
        enableAPI: boolean;
      };
    }>('/config'),

  updateConfig: (config: {
    ai?: {
      defaultModel?: string;
      maxTokensPerCall?: number;
      maxTokensPerDay?: number;
      enabledModels?: string[];
    };
    limits?: {
      maxWorkflowsPerUser?: number;
      maxFileSize?: number;
      maxExecutionTime?: number;
      maxNodesPerWorkflow?: number;
    };
    features?: {
      enableAI?: boolean;
      enableTeams?: boolean;
      enableTemplates?: boolean;
      enableAPI?: boolean;
    };
  }) =>
    api.put<void>('/config', config),
};

export default api;
